import {castArray} from '@coolcolduk/util';
import {useEffect} from 'react';

/**
 * Union type for all valid event types across HTMLElement, Window, and Document
 */
type AllEventTypes = keyof HTMLElementEventMap | keyof WindowEventMap | keyof DocumentEventMap;

/**
 * Get the event type for a given event name, prioritizing HTMLElementEventMap,
 * then WindowEventMap, then DocumentEventMap
 */
type EventMapForType<K extends AllEventTypes> = K extends keyof HTMLElementEventMap
  ? HTMLElementEventMap[K]
  : K extends keyof WindowEventMap
    ? WindowEventMap[K]
    : K extends keyof DocumentEventMap
      ? DocumentEventMap[K]
      : Event;

/**
 * Hook to attach an event listener to a DOM element, window, or document
 * @param target The target element to attach the listener to - can be a ref, direct element, or null
 * @param type The event type to listen for (e.g. 'click', 'scroll')
 * @param listener The event listener callback function
 * @param addOptions Options for addEventListener (capture, passive, etc)
 * @param removeOptions Options for removeEventListener (capture)
 * @deprecated update the one in common-hook
 */
export function useEventListener<T extends HTMLElement | Window | Document, K extends AllEventTypes>(
  target: React.RefObject<T> | T | null,
  type: K | K[],
  listener: ((this: T, ev: EventMapForType<K>) => void) | {handleEvent: (ev: EventMapForType<K>) => void},
  addOptions?: boolean | AddEventListenerOptions,
  removeOptions?: boolean | EventListenerOptions,
): void {
  useEffect(() => {
    const element = target && 'current' in target ? target.current : target;

    if (!element) return;

    const typeArray = castArray(type);

    typeArray.forEach((type) =>
      element.addEventListener(type, listener as EventListenerOrEventListenerObject, addOptions),
    );

    return () => {
      typeArray.forEach((type) =>
        element.removeEventListener(type, listener as EventListenerOrEventListenerObject, removeOptions),
      );
    };
  }, [target, type, listener, addOptions, removeOptions]);
}
